import type { FileMetadata, Product, StreamProduct } from '@igus/iglidur-free-form-hub-interfaces';
import { UnitSystem } from '@igus/kopla-domain';
import { useTranslation } from 'next-i18next';
import { displayPrice } from '@hub/libs/product-price-utils';
import { useSessionStore } from '@hub/hooks/states/useSessionStore';
import { useAddToShoppingCart } from '@hub/free-form-hub/free-form-hub-details/BuyBox/useAddToShoppingCart';
import { Icon, IconColor, IconID } from '@hub/common/Icon/Icon';
import { useEffect } from 'react';
import { BuyBoxModals } from '@hub/common/BuyBox/modals/BuyBoxModals';
import { BoreholeAndChannelLimitationsModal } from '@hub/free-form-hub/common/borehole-limitations/BoreholeAndChannelLimitationsModal';
import { ProgressIndicator, ProgressShape } from '@icommerce/ui';

type PriceInfoProps = {
  product: Product | StreamProduct;
  quantity: number;
  uploadId: string;
  metaData: FileMetadata;
  modelThumbnail?: string;
  showToast?: (product: Product) => void;
};
export const PriceInfo = ({ product, quantity, uploadId, metaData, modelThumbnail, showToast }: PriceInfoProps) => {
  const session = useSessionStore(state => state.session);
  const { t, i18n } = useTranslation(['category/productFilter', 'common']);

  const {
    addToShoppingCart,
    setActiveModal,
    activeModal,
    loading,
    boreholeAndChannelCheck,
    boreholeLimitationsModalVisible,
    closeBoreholeLimitationsModal,
    addToShoppingCartAfterBoreholeAcknowledgement,
    designerUrl,
  } = useAddToShoppingCart({
    session,
    quantity,
    uploadId,
    origin: product?.origin,
    materialName: product?.material.name,
    materialId: product?.material.id,
    metaData,
    modelThumbnail,
    procedure: product?.material.procedure,
    priceType: product?.price?.type,
  });

  const isUponRequest = product.price?.unit === -1;
  const isNonBindingIcnOrigin = product.origin === 'icn' && product.price?.type === 'non-binding';
  const isPending = 'status' in product && product.status === 'pending';
  const showPriceLoader = isPending || !product.price;

  const priceAsterisk = () => <span className="text-igus-primary"> *</span>;

  const addToBasketButton = () => {
    if (loading) {
      return (
        <div className="absolute w-7 h-7 rounded-full animate-spin border-solid border-[#F07D00] border-t-transparent border-2 " />
      );
    }
    return product.price?.unit > 0 && !isNonBindingIcnOrigin ? (
      <Icon id={IconID.ADD_SHOPPING_CART} size={9} color={IconColor.ORANGE} />
    ) : (
      <Icon id={IconID.REQUEST_QUOTE} size={9} color={IconColor.ORANGE} />
    );
  };

  useEffect(() => {
    if (activeModal === BuyBoxModals.ADD_TO_SHOPPING_CART && (!('status' in product) || product.status === 'ready')) {
      showToast?.(product);
      setActiveModal(null);
    }
  }, [activeModal]);

  if (showPriceLoader) {
    return (
      <ProgressIndicator
        label={t('COMMON.LOADING', { ns: 'free-form-hub/freeFormHubCommon' })}
        shape={ProgressShape.Circular}
        size={28}
        dataTestId="price-loading-spinner"
        className="block mx-auto my-2"
      />
    );
  }

  return (
    <>
      <div data-testid="table_unit_price" className="z-5 flex flex-1 flex-col md:text-right">
        <span>
          {isUponRequest
            ? t(`COMMON.UPON_REQUEST`, { unit: UnitSystem.metric, ns: 'free-form-hub/freeFormHubCommon' })
            : displayPrice(product.price.unit, product.price.currency, i18n.language)}
          {!isUponRequest && isNonBindingIcnOrigin && priceAsterisk()}
          {typeof product.price.details !== 'undefined' &&
            !Number.isNaN(product.price.details?.toolCost) &&
            product.price.details?.toolCost > 0 && (
              <>
                {priceAsterisk()}
                <p className="text-xxs mt-1 text-gray-300 whitespace-nowrap">
                  {t(`PRICE_PLUS_SETUP_COSTS_SHORT`, {
                    price: displayPrice(product.price.details?.toolCost, product.price.currency, i18n.language),
                    ns: 'free-form-hub/freeFormHub',
                  })}{' '}
                  {priceAsterisk()}
                </p>
              </>
            )}
        </span>
        <button
          data-testid="add_to_cart_button"
          type="button"
          className="z-30 items-center place-self-end justify-center rounded-full w-12 h-12 hover:bg-black/[0.04] md:flex hidden"
          onClick={event => {
            event.stopPropagation();
            void addToShoppingCart();
          }}
        >
          {addToBasketButton()}
        </button>
      </div>
      {boreholeAndChannelCheck && (
        <BoreholeAndChannelLimitationsModal
          visible={boreholeLimitationsModalVisible}
          procedure={product.material.procedure}
          limits={boreholeAndChannelCheck.limits}
          designerUrl={designerUrl}
          onClose={closeBoreholeLimitationsModal}
          onConfirm={addToShoppingCartAfterBoreholeAcknowledgement}
        />
      )}
    </>
  );
};
